import React, { useCallback, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import CardInfo from '../../../../../components/CardInfo/CardInfo'
import InputText from '../../../../../components/InputText/InputText'
import RadioButton from '../../../../../components/RadioButton/RadioButton'
import CustomDivider from '../../../../../components/CardInfo/components/CustomDivider'
import DragAndDrop from '../../../../../components/DragAndDrop/DragAndDrop'
import CellLocation from '../../../../../components/Table/components/CellLocation'
import { useFormik } from 'formik'
import {
    initialValues,
    validationSchema,
} from '../../../../../forms/CreateRepairRequestForm'
import useUploadFile from '../../../../../hooks/useUploadFile'
import useGlobalLoading from '../../../../../hooks/useGlobalLoading'
import { XIcon } from '@heroicons/react/solid'
import useScreen from '../../../../../hooks/useScreen'
import { useNetworkStatus } from '../../../../../utils/networkStatus'

const ImageItem = ({ src, onRemove }) => {
    return (
        <div className="lg:w-16 lg: h-16 w-auto h-auo group overflow-visible relative">
            <div
                onClick={onRemove}
                role="button"
                className="w-6 h-6 flex items-center justify-center rounded-full bg-error cursor-pointer  absolute -top-3 -right-3"
            >
                <XIcon className="text-white w-4 h-4" />
            </div>
            <img src={src} className="object-cover w-full h-full rounded-lg" />
        </div>
    )
}

ImageItem.propTypes = {
    src: PropTypes.string,
    onRemove: PropTypes.oneOfType([PropTypes.func]),
}

const RenderCustomCard = ({ title, content }) => {
    return (
        <div className="w-full h-auto flex flex-col items-center justify-start overflow-hidden lg:rounded-3xl lg:border border-grayLines">
            <div className="w-full h-auto bg-pgBackground lg:p-6 p-4 lg:border-0 border-b border-grayLines">
                <p className="text-black70 text-base font-medium">{title}</p>
            </div>
            <div className="w-full h-auto flex flex-col items-center justify-start bg-white lg:p-6 p-4">
                {content}
            </div>
        </div>
    )
}

RenderCustomCard.propTypes = {
    title: PropTypes.string,
    content: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.element,
        PropTypes.any,
    ]),
}

const CreateRepairRequestForm = ({
    asset,
    formRef,
    onSubmitForm,
    setError,
    setValidForm,
    isEstimate,
    successfulSubmit,
    setSuccessfulSubmit,
    resetTouch,
    setResetTouch,
    images,
    photosIds,
    setImages,
    setPhotosIds,
}) => {
    const { isMobile, isTablet } = useScreen()
    const [filesSuccess, setFileSuccess] = useState([])
    const { handleUpdateFile } = useUploadFile()
    const { changeLoading } = useGlobalLoading()
    const { isOffline } = useNetworkStatus()

    const onRemoveFile = index => {
        var imagesL = [...images]
        var photoIdsL = [...photosIds]

        imagesL.splice(index, 1)
        photoIdsL.splice(index, 1)

        setImages(imagesL)
        setPhotosIds(photoIdsL)
    }

    const handleImageSuccess = response => {
        const result = response?.data
        let imagesL = images
        let photoIdsL = photosIds

        imagesL.push({ path: result.filepath })
        photoIdsL.push(result.filename)

        setPhotosIds(photoIdsL)
        changeLoading(false)
    }

    const handleImageError = result => {
        const errorMessage =
            result.toString().indexOf('File is too large') > -1
                ? 'File is too large. 8MB Max'
                : result.toString().indexOf('422') > -1
                ? result.toString().substring(10)
                : 'Unable to upload file.'
        setError(errorMessage)
        changeLoading(false)
    }

    const savedState = localStorage.getItem('repairRequestForm')
    let storedValues
    try {
        storedValues = savedState ? JSON.parse(savedState) : initialValues
    } catch (error) {
        console.error('Error parsing stored form data:', error)
        storedValues = initialValues
    }

    const {
        getFieldProps,
        values,
        errors,
        setFieldValue,
        touched,
        handleSubmit,
        isValid,
        resetForm,
        setTouched,
    } = useFormik({
        initialValues: storedValues,
        validationSchema,
        onSubmit: formValues => {
            try {
                onSubmitForm(formValues)
            } catch (error) {
                console.error('Submit Error:', error)
            }
        },

        validateOnMount: true,
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
    })

    useEffect(() => {
        if (successfulSubmit) {
            resetForm({ values: initialValues })
            setSuccessfulSubmit(false)
            setValidForm(false)
            localStorage.removeItem('repairRequestState')
            localStorage.removeItem('repairRequestForm')
        }
    }, [successfulSubmit])

    useEffect(() => {
        const hasOneTouched = Object.keys(touched)?.length > 0
        if (hasOneTouched) {
            setError(null)
        }
    }, [touched])

    useEffect(() => {
        setError(null)
    }, [values])

    useEffect(() => {
        if (resetTouch) {
            setResetTouch(false)
            setTouched({})
        }
    }, [resetTouch])

    useEffect(() => {
        setValidForm(isValid)
    }, [isValid])

    useEffect(() => {
        if (Object.keys(values).length > 0) {
            localStorage.setItem('repairRequestForm', JSON.stringify(values))
        }
    }, [values])

    const hasOneTouched = Object.keys(touched)?.length > 0

    const onRemoveFileFunc = index => {
        var filesF = [...filesSuccess]
        filesF.splice(index, 1)
        setFileSuccess(filesF)
        if (onRemoveFile) {
            onRemoveFile(index)
        }
    }

    const handleImageSuccessFunc = (resp, onAdded) => {
        onAdded()
        handleImageSuccess(resp)
    }

    const offlineImageUpload = (file, successAdded) => {
        setImages(prevImages => [...prevImages, file])
        successAdded?.push(file)
        changeLoading(false)
    }

    const imageUpload = (formData, handleImageError, successAdded, file) => {
        offlineImageUpload(file, successAdded)

        if (!isOffline) {
            handleUpdateFile(
                formData,
                handleImageSuccessFunc,
                handleImageError,
                () => {
                    successAdded?.push(file)
                },
            )
        }
    }

    const setFilesFunc = useCallback(
        async val => {
            if (
                filesSuccess?.length <= 5 &&
                val?.length <= 5 &&
                filesSuccess?.length + val?.length <= 5
            ) {
                let successAdded = []
                await Promise.all(
                    val?.map(file => {
                        const formData = new FormData()
                        formData.append('file', file)
                        changeLoading(true)
                        imageUpload(
                            formData,
                            handleImageError,
                            successAdded,
                            file,
                        )
                    }),
                )
                setFileSuccess([...filesSuccess, ...successAdded])
            } else {
                setError('You can not upload more than 5 photos')
            }
        },
        [filesSuccess],
    )

    const renderImagesMemo = useMemo(() => {
        return filesSuccess?.map((item, index) => {
            const srcImage = URL.createObjectURL(item)
            return (
                <ImageItem
                    key={srcImage}
                    src={srcImage}
                    onRemove={() => onRemoveFileFunc(index)}
                />
            )
        })
    }, [filesSuccess])

    const renderDescriptionOfIssue = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start">
                <div className="flex lg:flex-row flex-col items-start justify-between gap-x-12 lg:gap-y-0 gap-y-1 w-full">
                    <InputText
                        {...getFieldProps('description')}
                        error={
                            (hasOneTouched &&
                                touched['description'] &&
                                errors['description']) ||
                            null
                        }
                        label="What needs to be done?"
                        isRequired
                        type="textarea"
                        placeholder={
                            isEstimate
                                ? 'Describe the issue the technician will be diagnosing for the estimate'
                                : 'Describe the issue'
                        }
                        additionalClass="w-full flex-grow"
                    />
                    <div className="w-full flex-grow flex flex-col items-start justify-start lg:mb-0 mb-6">
                        <p className="text-textDarkerGray font-medium text-sm mb-2">
                            Is the asset down?
                        </p>
                        <RadioButton
                            checked={true}
                            value={values['assetIsDown']}
                            onChange={() => {
                                setFieldValue(
                                    'assetIsDown',
                                    !values['assetIsDown'],
                                )
                            }}
                            label="Yes, asset is down "
                        />
                    </div>
                </div>
                {!isMobile && <CustomDivider />}
                <div className="flex lg:flex-row flex-col items-start justify-between gap-x-12 gap-y-1 w-full">
                    <InputText
                        {...getFieldProps('faultCode')}
                        label="Fault Code Display"
                        type="text"
                        placeholder="Are there any error codes?"
                        additionalClass="w-full flex-grow"
                        hasError={false}
                    />
                    <InputText
                        {...getFieldProps('locationDetails')}
                        label="Where on site is it?"
                        type="text"
                        placeholder="Please tell us where we can find the asset"
                        additionalClass="w-full flex-grow"
                        hasError={false}
                    />
                </div>
            </div>
        )
    }

    const renderPurchaseOrder = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start">
                <div className="flex lg:flex-row flex-col items-start justify-between gap-x-12 lg:gap-y-0 gap-y-1 w-full">
                    <InputText
                        {...getFieldProps('poNumber')}
                        error={errors['poNumber']}
                        label="PO Number"
                        type="text"
                        placeholder="Provide PO number, if applicable"
                        additionalClass="w-full flex-grow"
                    />
                </div>
            </div>
        )
    }

    const renderOtherDetails = () => {
        let assetName = asset?.name
        if (asset?.customer_alias || asset?.customerAlias) {
            assetName = asset?.customer_alias
                ? asset?.customer_alias
                : asset?.customerAlias
        }
        const locationLabel = asset?.shippingName || asset?.location
        const locationAddress = asset?.shippingAddress || asset?.address
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start">
                <div className="flex lg:flex-row flex-col items-start justify-between gap-x-12 lg:gap-y-0 gap-y-1 w-full">
                    <InputText
                        label="Customer Unit #"
                        type="text"
                        additionalClass="w-full flex-grow"
                        disabled={true}
                        value={assetName}
                    />
                    <InputText
                        label="Asset Location"
                        type="textarea"
                        additionalClass="w-full flex-grow"
                        disabled={true}
                        hintText="Wrong location? Let us know below"
                        isCellLocation
                        value={
                            (
                                <CellLocation
                                    otherClass="bg-grayBackground  w-full px-4 py-3 cursor-default overflow-hidden outline-none border border-grayLines rounded-xl"
                                    label={locationLabel}
                                    address={locationAddress}
                                />
                            ) || '-'
                        }
                    />
                </div>
                {!isMobile && <CustomDivider />}
                <div className="flex flex-row items-start justify-between gap-x-12 w-full lg:mt-0 mt-6">
                    <InputText
                        {...getFieldProps('additionalDetails')}
                        label="What else should we know?"
                        type="textarea"
                        placeholder={
                            isEstimate
                                ? 'Add any other relevant details about the estimate'
                                : 'Add any other relevant details'
                        }
                        additionalClass="w-full flex-grow"
                        hintText="Example: For any questions, call Don at 803.555.5555"
                        error={errors['additionalDetails']}
                    />
                </div>
            </div>
        )
    }

    const renderForm = () => {
        return (
            <div className="w-full h-auto flex flex-col items-center jusitfy-start lg:gap-y-6 ">
                <RenderCustomCard
                    title="Description of Issue"
                    content={renderDescriptionOfIssue()}
                />
                <RenderCustomCard
                    title="Purchase Order"
                    content={renderPurchaseOrder()}
                />
                <RenderCustomCard
                    title="Other Details"
                    content={renderOtherDetails()}
                />
                <div className="flex w-full flex-col lg:p-0 p-4 items-center justify-center transition-all">
                    <DragAndDrop setFiles={setFilesFunc} />
                    {filesSuccess?.length > 0 && (
                        <div className="w-full h-auto lg:flex grid grid-cols-3 items-center justify-start flex-wrap max-w-full mt-6 gap-x-4 gap-y-6">
                            {renderImagesMemo}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <form
            ref={isValid ? formRef : null}
            onSubmit={handleSubmit}
            id="create-repair-form"
            className="lg:w-240 w-full h-auto m-auto b"
        >
            <CardInfo
                title={
                    isEstimate ? 'Add Estimate Details' : 'Add Request Details'
                }
                subTitle="Tell us a little more about the issue"
                content={renderForm()}
                hasRounded={!isMobile && !isTablet}
                hasPaddingContent={!isMobile && !isTablet}
            />
        </form>
    )
}

CreateRepairRequestForm.propTypes = {
    asset: PropTypes.object,
    formRef: PropTypes.object,
    onSubmitForm: PropTypes.func,
    setError: PropTypes.func,
    setValidForm: PropTypes.func,
    isEstimate: PropTypes.bool,
    successfulSubmit: PropTypes.bool,
    setSuccessfulSubmit: PropTypes.func,
    resetTouch: PropTypes.bool,
    setResetTouch: PropTypes.func,
    images: PropTypes.array.isRequired,
    photosIds: PropTypes.array.isRequired,
    setImages: PropTypes.func.isRequired,
    setPhotosIds: PropTypes.func.isRequired,
}

export default CreateRepairRequestForm
